import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Grid
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { get } from '../../services/apiService';

const SnykProjectTargetTable = ({ showUnresolved = true, outOfSLA = false, collection = '' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dueDate, setDueDate] = useState(null);

  useEffect(() => {
    fetchData();
  }, [dueDate, showUnresolved, outOfSLA, collection]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const queryParams = new URLSearchParams();
      if (outOfSLA) {
        queryParams.append('outOfSLA', 'true');
      } else {
        if (dueDate) {
          const dateStr = dayjs(dueDate).format('YYYY-MM-DD');
          queryParams.append('dueDate', dateStr);
        }
        if (showUnresolved) {
          queryParams.append('showUnresolved', 'true');
        }
      }
      if (collection) {
        queryParams.append('projectCollection', collection);
      }
      const queryString = queryParams.toString();
      const url = `/api/snyk/project-target-summary${queryString ? `?${queryString}` : ''}`;
      const response = await get(url);
      
      if (response && response.success && response.data) {
        setData(response.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching project target summary:', err);
      setError(err.message || 'Failed to fetch project target data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Critical, High & Medium Issues by Project Target (Total: 0)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  // Calculate total sum of all "Total" values
  const totalCount = data.reduce((sum, row) => sum + (row.total || 0), 0);

  // Split data into 3 roughly equal chunks
  const chunkSize = Math.ceil(data.length / 3);
  const chunks = [];
  for (let i = 0; i < data.length; i += chunkSize) {
    chunks.push(data.slice(i, i + chunkSize));
  }

  // Render a table for a chunk of data
  const renderTable = (tableData, tableIndex) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell><strong>Project Target</strong></TableCell>
            <TableCell align="right"><strong>Critical</strong></TableCell>
            <TableCell align="right"><strong>High</strong></TableCell>
            <TableCell align="right"><strong>Medium</strong></TableCell>
            <TableCell align="right"><strong>Total</strong></TableCell>
            <TableCell align="right"><strong>Common Library</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row, index) => (
            <TableRow key={`${tableIndex}-${index}`} hover>
              <TableCell sx={{ fontSize: '0.875rem' }}>
                {(row.projectTarget || 'Unknown').replace(/^tr\//, '')}
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={row.critical || 0}
                  size="small"
                  color="error"
                  variant={row.critical > 0 ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={row.high || 0}
                  size="small"
                  color="warning"
                  variant={row.high > 0 ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={row.medium || 0}
                  size="small"
                  color="info"
                  variant={row.medium > 0 ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell align="right">
                <strong>{row.total || 0}</strong>
              </TableCell>
              <TableCell align="right">
                {row.commonLibraryIssuesCount || 0}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h6" gutterBottom>
          Critical, High & Medium Issues by Project Target (Total: {totalCount})
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Filter by Due Date"
            value={dueDate}
            onChange={(newValue) => setDueDate(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 200 }
              },
              actionBar: {
                actions: ['clear', 'today']
              }
            }}
          />
        </LocalizationProvider>
      </Box>
      <Grid container spacing={2}>
        {chunks.map((chunk, index) => (
          <Grid item xs={12} md={4} key={index}>
            {renderTable(chunk, index)}
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default SnykProjectTargetTable;

